/**
 * HookItem Component
 * Single draggable hook item in the pipeline
 */

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useI18n } from '@/i18n';
import type { HookConfig } from '../../types/hooks';

// Mapping of hook names to their config section keys (for i18n lookup)
const HOOK_CONFIG_SECTION_KEYS: Record<string, string> = {
  user_facts: 'memory_query',
  first_message: 'first_message',
  classifier: 'response_classifier',
  memory_context: 'memory_query',
  memory_reinforce: 'memory_ebbinghaus',
};

interface HookItemProps {
  hook: HookConfig;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
}

export const HookItem: React.FC<HookItemProps> = ({
  hook,
  isSelected,
  isDragging: globalIsDragging,
  onSelect,
  onToggleVisibility,
}) => {
  const { getSectionTitle, messages } = useI18n();

  // Get i18n translations for hook display_name and description
  const hookDef = messages.hooks.hookDefinitions[hook.hook_name as keyof typeof messages.hooks.hookDefinitions];
  const displayName = hookDef?.displayName || hook.display_name;
  const description = hookDef?.description || hook.description;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isThisDragging,
  } = useSortable({
    id: `hook-${hook.stage}-${hook.hook_name}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (globalIsDragging) return;

    const target = e.target as HTMLElement;
    if (target.closest('.hook-toggle')) {
      e.stopPropagation();
      onToggleVisibility();
      return;
    }

    if (target.closest('.hook-drag-handle')) return;

    onSelect();
  };

  const itemClasses = [
    'flex items-center gap-2 p-2 bg-white border rounded cursor-pointer',
    'hover:bg-gray-50 transition-colors',
    isSelected ? 'ring-2 ring-blue-500' : '',
    isThisDragging ? 'opacity-50' : '',
  ].filter(Boolean).join(' ');

  const toggleClasses = [
    'hook-toggle w-10 h-5 rounded-full transition-colors relative',
    hook.show_in_settings ? 'bg-green-500' : 'bg-gray-300',
  ].join(' ');

  const toggleKnobClasses = [
    'w-4 h-4 bg-white rounded-full shadow transform transition-transform absolute top-0.5',
    hook.show_in_settings ? 'translate-x-5' : 'translate-x-0.5',
  ].join(' ');

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-hook-name={hook.hook_name}
      className={itemClasses}
      onClick={handleClick}
    >
      <div
        className="hook-drag-handle cursor-move text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8h16M4 16h16"
          />
        </svg>
      </div>

      <button
        type="button"
        className={toggleClasses}
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility();
        }}
      >
        <div className={toggleKnobClasses} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{displayName}</span>
          {HOOK_CONFIG_SECTION_KEYS[hook.hook_name] && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-50 text-blue-600 rounded border border-blue-100">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {getSectionTitle(HOOK_CONFIG_SECTION_KEYS[hook.hook_name])}
            </span>
          )}
        </div>
        {description && (
          <div className="text-xs text-gray-500 truncate">{description}</div>
        )}
      </div>

      <span className="text-xs text-gray-400 font-mono">#{hook.sort_order}</span>
    </div>
  );
};
